import clsx from "clsx";
import PropTypes from "prop-types";
import React from "react";
import { Pie } from "react-chartjs-2";
import "./PieChart.scss";

function resolveCanvasColor(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("var(")) return value;

  // Support `var(--token)` and `var(--token, fallback)`
  const match = /^var\(\s*([^,\s)]+)\s*(?:,\s*(.+?)\s*)?\)$/.exec(trimmed);
  if (!match) return value;

  const token = match[1];
  const fallback = match[2];

  if (typeof window === "undefined" || !document?.documentElement) {
    return fallback || value;
  }

  const resolved = window.getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return resolved || (fallback ? fallback.trim() : value);
}

function useDataBsThemeVersion() {
  const [version, setVersion] = React.useState(0);
  React.useEffect(() => {
    const el = document.documentElement;
    if (typeof MutationObserver === "undefined") {
      return undefined;
    }
    const obs = new MutationObserver(() => {
      setVersion((v) => v + 1);
    });
    obs.observe(el, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => obs.disconnect();
  }, []);
  return version;
}

const propTypes = {
  /**
   * Data containing complete inputs for drawing pie chart.
   */
  pieData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    })
  ).isRequired,
  /**
   * Heading label for PieChart
   */
  heading: PropTypes.string.isRequired
};

const options = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      left: 8,
      right: 8,
      top: 4,
      bottom: 4
    }
  },
  // PDF / print: static chart avoids raster seams; no hover animation.
  animation: { duration: 0 },
  hover: { animationDuration: 0 },
  responsiveAnimationDuration: 0,
  plugins: {
    datalabels: {
      display: false
    }
  },
  title: { display: false },
  legend: { display: false },
  events: []
};

const formatPieData = (pieData) => {
  const res = {
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
        // Match stroke to fill so Chart.js / Chromium PDF does not draw white wedges between arcs.
        borderColor: [],
        borderWidth: 0,
        hoverBorderWidth: 0
      }
    ]
  };
  pieData.forEach((pie) => {
    res.labels.push(pie.label);
    // Chart.js draws to canvas; CSS variables must be resolved to concrete colors.
    const fill = resolveCanvasColor(pie.color);
    res.datasets[0].backgroundColor.push(fill);
    res.datasets[0].borderColor.push(fill);
    res.datasets[0].data.push(pie.value);
  });
  return res;
};

function PieChart(props) {
  const { pieData, heading } = props;

  const dataBsThemeVersion = useDataBsThemeVersion();

  return (
    <div className={clsx("Uic-PieChart-base")}>
      <h6>{heading}</h6>
      <div className={clsx("Uic-PieChart-main_section")}>
        <div className={clsx("Uic-PieChart-legend_col")}>
          {pieData?.map((item) => (
            <div key={item.label} className={clsx("Uic-PieChart-section_info")}>
              <div
                className={clsx("Uic-PieChart-dot")}
                style={{ backgroundColor: resolveCanvasColor(item?.color) }}
              />

              <div className={clsx("Uic-PieChart-sub_div")}>
                <span className={clsx("Uic-PieChart-label_text")}>{item.label}</span>
                <span className={clsx("Uic-PieChart-value_text")}>{item?.value}</span>
              </div>
            </div>
          ))}
        </div>
        <div className={clsx("Uic-PieChart-section_chart")}>
          <Pie
            // Remount on every theme change (including light → dark → light) so canvas colors refresh.
            key={dataBsThemeVersion}
            options={options}
            data={formatPieData(pieData)}
          />
        </div>
      </div>
    </div>);

}

PieChart.propTypes = propTypes;

export default PieChart;
